import { consommerLancement } from '@/api/auth'
import { entreeReussie } from './porte.js'

/**
 * LE RETOUR DE LA PAGE DE CONNEXION.
 *
 * ─── D'où vient ce laissez-passer ────────────────────────────────────────────
 *
 * L'application n'a plus d'écran de connexion à elle : quand elle a besoin
 * d'une identité, elle envoie vers la page de connexion du trousseau — la
 * porte, servie par management, la même pour tout le parc. Cette page rend la
 * main ici, avec un laissez-passer à usage unique.
 *
 * ─── Pourquoi dans le FRAGMENT, et pas dans l'adresse ────────────────────────
 *
 * Ce qui suit un `#` ne quitte JAMAIS le navigateur : ce n'est envoyé à aucun
 * serveur. Un jeton placé dans la requête, lui, finirait dans les journaux
 * d'accès de ce serveur, dans ceux de son proxy, et dans l'en-tête `Referer`
 * de la première image chargée. Pour un laissez-passer qui ouvre une session,
 * c'est la différence entre un secret et une trace publique.
 *
 * ─── Effacé AVANT d'être consommé ────────────────────────────────────────────
 *
 * Le fragment est retiré de la barre d'adresse tout de suite, et pas après :
 * il survivrait autrement à un rechargement, à un signet, à un partage de lien.
 * Il est à usage unique — le rejouer ne donnerait rien — mais un secret périmé
 * reste un secret qui traîne.
 */

const CLE = 'lancement'

/**
 * Lit ET efface le laissez-passer, qu'il arrive dans le FRAGMENT ou dans la
 * REQUÊTE. `null` s'il n'y en a pas.
 *
 * DEUX PROVENANCES, et la première est la bonne. Le retour de la porte arrive
 * dans le fragment — voir l'en-tête. La forme `?lancement=jeton` est l'autre
 * chemin d'entrée du parc : une application qui lance le backoffice arrive
 * ainsi, et c'est un contrat entre deux briques, pas un confort d'URL. Elle
 * était consommée par l'écran « Portail de services » ; cet écran a disparu, ce
 * chemin d'entrée non — le laisser tomber avec la page aurait cassé en silence
 * tout lancement venu d'une autre application.
 *
 * Le fragment est préféré quand les deux sont présents : c'est le chemin qui ne
 * laisse aucune trace serveur.
 */
export function preleverLancement(): string | null {
  const duFragment = preleverDuFragment()
  if (duFragment !== null) return duFragment
  return preleverDeLaRequete()
}

function preleverDuFragment(): string | null {
  const brut = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : ''
  if (brut === '') return null

  const parametres = new URLSearchParams(brut)
  const jeton = parametres.get(CLE)
  if (jeton === null || jeton === '') return null

  // On efface AVANT de s'en servir — voir l'en-tête. `replaceState` plutôt
  // qu'une écriture du hash : celle-ci ajouterait une entrée à l'historique, et
  // le bouton « précédent » ramènerait sur l'adresse porteuse du jeton.
  parametres.delete(CLE)
  const reste = parametres.toString()
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}${reste === '' ? '' : `#${reste}`}`,
  )
  return jeton
}

function preleverDeLaRequete(): string | null {
  const parametres = new URLSearchParams(window.location.search)
  const jeton = parametres.get(CLE)
  if (jeton === null || jeton === '') return null

  // Effacé aussitôt : arrivé par la requête, il est DÉJÀ dans les journaux du
  // serveur et du proxy. Le retirer de la barre d'adresse évite au moins qu'il
  // reste dans l'historique, un signet ou un lien recopié.
  parametres.delete(CLE)
  const reste = parametres.toString()
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${reste === '' ? '' : `?${reste}`}${window.location.hash}`,
  )
  return jeton
}

/**
 * Ouvre la session si l'on revient de la page de connexion. Rend `true` quand
 * une session a été ouverte.
 *
 * Un échec n'est pas masqué mais ne bloque rien : le laissez-passer a pu être
 * rejoué (rechargement d'un onglet resté ouvert) ou expirer. L'application
 * poursuit alors son démarrage normal, constate qu'elle n'a pas de session, et
 * renvoie à la porte — ce qui est exactement le bon geste.
 */
export async function consommerRetour(): Promise<boolean> {
  const jeton = preleverLancement()
  if (jeton === null) return false
  try {
    await consommerLancement(jeton)
    // Le tour a ABOUTI : on remet à zéro le compteur du garde-fou, sans quoi il
    // finirait par bloquer quelqu'un qui se connecte normalement plusieurs fois
    // dans le même onglet.
    entreeReussie()
    return true
  } catch {
    return false
  }
}
